// src/lib/auth/portalAuthClient.ts
import {
  signIn,
  fetchAuthSession,
  signOut,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
  associateWebAuthnCredential,
  listWebAuthnCredentials,
  deleteWebAuthnCredential,
} from "aws-amplify/auth";
import { decodeJWT } from "aws-amplify/auth";

import { ENV } from "@/config/env";
import {
  AUTH_ISSUE_CODE,
  WHOAMI,
  AUTH_REFRESH,
  AUTH_ME,
  AUTH_RESOLVE_ALIAS,
} from "@/utils/const";
import {
  clearPasskeyOnThisBrowser,
  getLastLoginId,
  markPasskeyOnThisBrowser,
} from "@/utils/storage";
import { apiPost, apiPatch } from "@shared-ui/utils/api";
import { callAuthRefresh } from "@shared-ui/utils/auth-api";
import { configureDebugTrace, trace } from "@shared-ui/utils/debugTrace";
import { buildProductRedirectUrl } from "./productRedirect";
import type {
  WhoamiMe,
  WhoamiResponse,
  ResolveAliasRequest,
  ResolveAliasResponse,
} from "@shared-domain/auth-service/contract";
import { RESOLVE_ALIAS_NOT_FOUND_PREFIX } from "@shared-domain/auth-service/contract";

// PRODUCT_DEV-549 計測: dev 環境でのみ永続デバッグトレースを有効化する。
// （prod/staging/顧客端末では no-op。任意の環境で一時的に見たい場合は当該ブラウザで
//  localStorage.__debugTrace="1" を手動セット。詳細は shared-ui/utils/debugTrace.ts）
configureDebugTrace(ENV.ENV === "dev");

/**
 * PRODUCT_DEV-641: alias 解決の使い回し期限。
 * 1 回のログイン（入力欄を離れる → 送信）を畳むのが目的なので短くてよい。
 */
const RESOLVE_ALIAS_CACHE_MS = 60_000;

/**
 * Portal の認証フローをまとめたクライアント（独自UI）
 *
 *  1. Self UI でサインインしトークンを取得する
 *  2. AuthService (/auth/issue-code) にトークンを送り、One-time code を発行
 *  3. プロダクトの URL に One-time code を付与してリダイレクト
 *  4. プロダクト側で One-time code からトークンを取得（本クラスの外）
 */
export class PortalAuthClientWithSelfUI {
  public isError: boolean = false;
  public error: string | null = null;
  public recoverySuggestion: string | null = null;

  // token 取得後
  private idToken: string | null = null;
  private accessToken: string | null = null;
  public me: WhoamiMe | null = null;

  // PRODUCT_DEV-396 「このブラウザでパスキー登録済」の DDB 同期を session 中1回だけ実行するためのフラグ
  // 旧 hasPasskey 移行と兼用 (旧データのユーザもこのフラグでガードされる)
  private passkeyDeviceSyncAttempted: boolean = false;

  // 検証コード取得後
  private exchangeCode: string | null = null;
  /**
   * PRODUCT_DEV-641: 直前の alias 解決の結果（1 件だけ）。
   * 同じログイン内で blur と送信の 2 回呼ばれるのを 1 回に畳むためのもの。
   */
  private resolveAliasCache: {
    key: string;
    value: { resolved: boolean; cognitoUsername: string; passkeyEnabled: boolean };
    expiresAt: number;
  } | null = null;
  private autoRedirect: boolean = false;

  // 認証チャレンジ状態
  public challengeType: string | null = null;
  public challengeParameters: Record<string, unknown> = {};

  /**
   * 0. 既存のトークンを設定する
   */
  async setTokens(idToken: string, accessToken: string) {
    this.idToken = idToken;
    this.accessToken = accessToken;
  }
  // 自動ログインフラグのセット
  async setAutoRedirect(flg: boolean) {
    this.autoRedirect = flg;
  }

  /**
   * 1. Self UI でサインインする
   */
  async signIn(username: string, password: string) {
    try {
      const user = await signIn({ username, password });
      console.log("Signed in user", user);
      await this.processSignInResult(user);
    } catch (error) {
      // 既存セッションエラーの場合、自動的にサインアウトして再試行
      if ((error as { name?: string }).name === "UserAlreadyAuthenticatedException") {
        console.log("既存セッションを検出、自動的にサインアウトして再試行します");
        trace(
          "auth-signout",
          "signin-retry-already-auth",
          { global: false },
          { stack: true },
        );
        await signOut({ global: false });
        const user = await signIn({ username, password });
        console.log("Signed in user (after retry)", user);
        await this.processSignInResult(user, " (after retry)");
        return;
      }
      throw error;
    }
  }

  /**
   * 1-1a. PRODUCT_DEV-421/484: tenantId + alias から cognitoUsername / passkey 可否を解決する
   *  - public endpoint (認証不要)
   *  - alias 不存在/tenant 不正でも resolve-alias はダミー cognitoUsername を返す
   *    → `resolved=false` で返し、呼び出し側が通常ログインID へフォールバックできる
   *  - passkeyEnabled は「この UA で登録済 AND テナント許可」のときのみ true (email は露出しない)
   */
  async resolveAlias(
    tenantId: string,
    alias: string,
  ): Promise<{ resolved: boolean; cognitoUsername: string; passkeyEnabled: boolean }> {
    /**
     * PRODUCT_DEV-641: **同じ入力値の 2 回目を投げない。**
     *
     * 1 回のログインで、入力欄を離れたとき（パスキー判定）と送信したとき
     * （サインイン）の 2 回この解決が要る。同じ値なら答えも同じなので、
     * 直前の結果を使い回す。この先はテナントの全ユーザを読む未認証 API で、
     * ユーザ数に比例して重くなる（PRODUCT_DEV-885）。
     *
     * **直前の 1 件だけ**持つ。入力を直せばキーが変わって自然に捨てられる。
     * 期限を短く切ってあるのは、画面を開きっぱなしにしたまま管理側で
     * エイリアスが変わった場合に古い答えを掴み続けないため（掴んでも
     * サインインが失敗するだけだが、原因が読めなくなる）。
     */
    const cacheKey = `${tenantId}\u0000${alias}`;
    const cached = this.resolveAliasCache;
    if (cached && cached.key === cacheKey && cached.expiresAt > Date.now()) {
      return cached.value;
    }
    const reqBody: ResolveAliasRequest = { tenantId, alias };
    // 認証不要 endpoint なので apiPost (Bearer token 自動付与) ではなく fetch を直接使う
    const res = await fetch(AUTH_RESOLVE_ALIAS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(reqBody),
    });
    if (!res.ok) {
      throw new Error("ユーザIDかパスワードが間違っています");
    }
    const data = (await res.json()) as ResolveAliasResponse;
    const resolved = !data.cognitoUsername.startsWith(
      RESOLVE_ALIAS_NOT_FOUND_PREFIX,
    );
    const value = {
      resolved,
      cognitoUsername: data.cognitoUsername,
      passkeyEnabled: Boolean(data.passkeyEnabled),
    };
    /**
     * **解決できたときだけ使い回す。**
     *
     * サーバは内部の失敗（DDB のスロットリング等）も 200 + ダミーに畳んで返す
     * （`resolve-alias.ts` の「例外時もエラーを返さずダミーを返す」）。未解決まで
     * キャッシュすると、**一時障害がこの端末で 60 秒間「ID かパスワードが違う」として
     * 固定され、押し直しても直らない**。未解決は畳まずに毎回問い合わせる
     * （1 ログインあたり最大 2 回に留まり、キャッシュの目的は失われない）。
     */
    if (resolved) {
      this.resolveAliasCache = {
        key: cacheKey,
        value,
        expiresAt: Date.now() + RESOLVE_ALIAS_CACHE_MS,
      };
    }
    return value;
  }

  /**
   * 1-1. PRODUCT_DEV-421: alias + tenantId でサインインする
   *  - resolve-alias で cognitoUsername を解決（public endpoint, 認証不要）
   *  - PRODUCT_DEV-484: alias が解決できなかった場合は、入力値を通常のログインID
   *    （メールアドレス等）として扱い signIn へフォールバックする。
   *    → エイリアスURLの画面でも通常ログインIDでログインできる。
   *  - 通常の signIn フローへ委譲（チャレンジ/エラーは signIn 側で統一処理）
   */
  async signInByAlias(tenantId: string, alias: string, password: string) {
    const { resolved, cognitoUsername } = await this.resolveAlias(
      tenantId,
      alias,
    );
    if (resolved) {
      try {
        await this.signIn(cognitoUsername, password);
      } catch (e) {
        // **失敗したら解決結果を捨てる。** alias の付け替え直後などに古い
        // cognitoUsername を 60 秒掴み続けると、正しいパスワードでも入れない。
        this.resolveAliasCache = null;
        throw e;
      }
    } else {
      // alias 未解決 → 入力値をそのまま通常ログインID として signIn（通常ログインと同一経路）
      await this.signIn(alias, password);
    }
  }

  /**
   * 1-2. 認証チャレンジに応答する
   */
  async confirmChallenge(challengeResponse: string, options?: Record<string, unknown>) {
    if (!this.challengeType) {
      throw new Error("チャレンジが設定されていません。");
    }

    const result = await confirmSignIn({
      challengeResponse,
      options,
    });

    console.log("Challenge response result:", result);
    await this.processSignInResult(result, " (after challenge)");
  }

  /**
   * signIn/confirmSignIn の結果を処理する共通メソッド
   * サインイン完了ならトークン取得、未完了ならチャレンジ状態を更新
   */
  private async processSignInResult(
    result: { isSignedIn: boolean; nextStep: { signInStep: string } },
    logSuffix = "",
  ) {
    if (result.isSignedIn) {
      await this.handleSuccessfulSignIn(logSuffix);
      return;
    }
    this.challengeType = result.nextStep.signInStep;
    this.challengeParameters = { ...(result.nextStep as Record<string, unknown>) };
    console.log(`Challenge required${logSuffix}:`, {
      type: this.challengeType,
      parameters: this.challengeParameters,
    });
  }

  /**
   * 2. /me を呼び出して自身の情報を取得
   */
  async whoami() {
    // const me = await fetch(ME);
    const { me } = await apiPost<WhoamiResponse>(WHOAMI);
    console.log("Who am I?", me);

    // ログインセッションからTokenを取得
    this.me = me;

    // PRODUCT_DEV-396 「このブラウザは passkeyDeviceKeys に未登録だが Cognito にパスキーを持っている」
    // 旧データユーザの自動補正。session 中1回のみ。
    // currentDeviceHasPasskey === false かつ Cognito 側にパスキーがある場合 → 現在のUAをDDBに追加
    if (
      me.currentDeviceHasPasskey === false &&
      !this.passkeyDeviceSyncAttempted
    ) {
      this.passkeyDeviceSyncAttempted = true;
      this.migrateCurrentDevicePasskey().catch((err) =>
        console.warn("migrateCurrentDevicePasskey failed:", err),
      );
    }
  }

  /**
   * PRODUCT_DEV-396 旧データ移行: Cognito にパスキーがあり、かつ現UAが
   * passkeyDeviceKeys に含まれていない場合、現UAを DDB に追加する。
   * fire-and-forget で呼ばれ、失敗しても本フローには影響しない。
   */
  private async migrateCurrentDevicePasskey(): Promise<void> {
    const list = await this.listPasskeys();
    if (list.length === 0) return; // Cognito 側にもパスキーが無いなら何もしない
    await this.requestAddPasskeyDevice();
  }

  /**
   * Amplify ストレージから Cognito refreshToken を取り出す（無ければ null）。
   * Amplify は `CognitoIdentityServiceProvider.<clientId>.<username>.refreshToken`
   * というキーで保持する。handleSuccessfulSignIn / issueOneTimeCode で共用する。
   */
  private readAmplifyRefreshToken(): string | null {
    try {
      const key = Object.keys(localStorage).find(
        (k) =>
          k.includes("CognitoIdentityServiceProvider") &&
          k.endsWith(".refreshToken"),
      );
      return key ? localStorage.getItem(key) : null;
    } catch (error) {
      console.warn("Failed to read refresh token from Amplify storage:", error);
      return null;
    }
  }

  /**
   * 4. AuthService の /auth/issue-code を叩いて One-time code を発行
   */
  async issueOneTimeCode(productId: string, tenantId: string): Promise<void> {
    if (!this.idToken || !this.accessToken) {
      throw new Error(
        "idToken / accessToken が未取得のため issue-code を呼べません。",
      );
    }

    // PRODUCT_DEV-549: refreshToken も連携先プロダクトへ渡す。
    // これを渡さないと exchange-code → product 側 localStorage の
    // `{store}-refreshToken` が空になり、shared-ui TokenProvider の先制リフレッシュ
    // (auth.ts: `... && cached.refresh`) が発火せず、idToken(24h) でセッションが切れて
    // 毎日ログインが必要になる。Amplify ストレージの値を第一参照とし（signIn 直後でも
    // setTokens 経由の自動ログイン復帰でも取得できる）、無ければ保存済み portal-refreshToken に fallback。
    const refreshToken =
      this.readAmplifyRefreshToken() ??
      localStorage.getItem("portal-refreshToken") ??
      undefined;

    const res = await apiPost<{
      success: boolean;
      status?: number;
      message?: string;
      body: {
        code?: string;
        expiresIn?: number;
      };
    }>(AUTH_ISSUE_CODE, {
      idToken: this.idToken,
      accessToken: this.accessToken,
      ...(refreshToken ? { refreshToken } : {}),
      productId,
      tenantId,
    });

    if (!res.success || !res.body.code || !res.body.expiresIn) {
      throw new Error(
        `issue-code failed: ${res.status} ${res.message ?? "unknown error"}`,
      );
    }

    this.exchangeCode = res.body.code;
  }

  /**
   * サインイン成功後のトークン取得と保存処理
   * @param logSuffix ログメッセージの接尾辞（デバッグ用）
   */
  async handleSuccessfulSignIn(logSuffix = ""): Promise<void> {
    const session = await fetchAuthSession();
    this.idToken = session.tokens?.idToken?.toString() || null;
    this.accessToken = session.tokens?.accessToken?.toString() || null;

    // refreshToken の取得と保存
    const refreshToken = this.readAmplifyRefreshToken();
    if (refreshToken) {
      localStorage.setItem("portal-refreshToken", refreshToken);
      console.log(`Refresh token saved to localStorage${logSuffix}`);
    }

    // localStorage への保存
    if (this.accessToken) {
      localStorage.setItem("portal-accessToken", this.accessToken);
    }
    if (this.idToken) {
      localStorage.setItem("portal-idToken", this.idToken);
    }

    // チャレンジ状態のクリア
    this.challengeType = null;
    this.challengeParameters = {};
  }

  /**
   * 5. プロダクト URL にリダイレクトする
   *    One-time code と state を URL フラグメントに付与
   */
  redirectProductUrl(url: string, options?: { returnTo?: string | null }): void {
    if (!this.exchangeCode) {
      throw new Error(
        "exchangeCode が存在しないためプロダクトへ遷移できません。",
      );
    }

    window.location.href = buildProductRedirectUrl(url, {
      authCode: this.exchangeCode,
      auto: this.autoRedirect,
      returnTo: options?.returnTo,
    });
  }

  /**
   * TOTP セットアップ: 秘密鍵とQRコード用URIを取得
   */
  async setupTOTP(): Promise<{ totpUri: string; secretKey: string }> {
    const totpSetupDetails = await setUpTOTP();
    const secretKey = totpSetupDetails.sharedSecret;
    const knownEnvs = ["dev", "stg", "prod"];
    const envSuffix = knownEnvs.includes(ENV.ENV)
      ? (ENV.ENV === "prod" ? "" : ` ${ENV.ENV}`)
      : " local";
    const appName = `Umeee Portal${envSuffix}`;
    // アカウント名: ユーザ名（ユーザID）
    const userName = this.me?.user.name || "";
    let loginId = "";
    if (this.idToken) {
      const decoded = decodeJWT(this.idToken);
      loginId = (decoded.payload["cognito:username"] as string) || "";
    }
    const accountName = loginId ? `${userName} (${loginId})` : userName;
    const totpUri = totpSetupDetails.getSetupUri(appName, accountName).toString();
    return { totpUri, secretKey };
  }

  /**
   * TOTP 検証 + MFA有効化: 6桁コードで検証し、TOTPをPreferredに設定
   */
  async verifyAndEnableTOTP(code: string): Promise<void> {
    await verifyTOTPSetup({ code });
    await updateMFAPreference({ totp: "PREFERRED" });
  }

  /**
   * MFA設定状況の確認: ユーザーのTOTP設定状態を取得
   */
  async checkMFAPreference(): Promise<{ isTOTPEnabled: boolean }> {
    const preference = await fetchMFAPreference();
    const isTOTPEnabled = preference.preferred === "TOTP" ||
      (preference.enabled?.includes("TOTP") ?? false);
    return { isTOTPEnabled };
  }

  /**
   * パスキー登録: ブラウザの生体認証をプロンプトして登録
   * 成功後、現在のブラウザを DDB Users.passkeyDeviceKeys に追加
   * (サーバが request UA を normalize してデバイスキー化)
   *
   * PRODUCT_DEV-1121: 登録できたら、このブラウザに印を付ける（ログイン画面はこの印があるときだけパスキーを自動で起動する）。
   * - 登録したパスキーの ID と種類は、一覧の一番新しいものから取る。スマホを QR で読んで登録したもの
   *   （`cross-platform`）には印を付けない
   * - この端末にすでに同じアカウントのパスキーがあると、Amplify は `PasskeyAlreadyExists` で失敗する。
   *   それは「このブラウザで使える」ということなので、エラーにせず印を付ける（二重には登録されない）
   */
  async registerPasskey(): Promise<"registered" | "alreadyOnThisDevice"> {
    const loginId = getLastLoginId();
    try {
      await associateWebAuthnCredential();
    } catch (err) {
      if ((err as { name?: string })?.name === "PasskeyAlreadyExists") {
        markPasskeyOnThisBrowser(loginId);
        await this.requestAddPasskeyDevice();
        return "alreadyOnThisDevice";
      }
      throw err;
    }
    markPasskeyOnThisBrowser(loginId, await this.newestPasskeyDetail());
    await this.requestAddPasskeyDevice();
    return "registered";
  }

  /** PRODUCT_DEV-1121 一番新しく登録されたパスキーの ID と種類（取れなければ空） */
  private async newestPasskeyDetail(): Promise<{ credentialId?: string; attachment?: string }> {
    try {
      const { credentials } = await listWebAuthnCredentials();
      const newest = [...credentials].sort(
        (a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0),
      )[0];
      return { credentialId: newest?.credentialId, attachment: newest?.authenticatorAttachment };
    } catch (err) {
      console.warn("listWebAuthnCredentials after register failed:", err);
      return {};
    }
  }

  /**
   * 登録済みパスキー一覧を取得
   */
  async listPasskeys(): Promise<{ credentialId: string; friendlyName?: string }[]> {
    const result = await listWebAuthnCredentials();
    return result.credentials
      .filter((c): c is typeof c & { credentialId: string } => !!c.credentialId)
      .map((c) => ({
        credentialId: c.credentialId,
        friendlyName: c.friendlyCredentialName,
      }));
  }

  /**
   * パスキーを削除
   * 削除後、DDB の passkeyDeviceKeys を常に全クリアする。
   * 個別 UA キー削除は credentialId と DDB エントリの紐付けが無いため不可。
   * 全クリアにより、削除した UA エントリが残ったまま auto-trigger され
   * ローカルにパスキー credential が無いのに WebAuthn ダイアログが起動して
   * Chrome 同期パスキー経由の QR フローに進む事象を防ぐ。
   * 他デバイスのパスキーは Cognito 側に残り、手動「パスキーでログイン」
   * もしくはログイン後の促進モーダルから再登録で auto-trigger も復活する。
   */
  async deletePasskey(credentialId: string): Promise<void> {
    await deleteWebAuthnCredential({ credentialId });
    // PRODUCT_DEV-1121: 消したパスキーの印を消す（パスキーの ID を持たない古い印も一緒に消す）
    clearPasskeyOnThisBrowser({ credentialId });
    try {
      await this.requestClearPasskeyDevices();
    } catch (err) {
      console.warn("clearPasskeyDevices after deletePasskey failed:", err);
    }
  }

  /**
   * DDB Users.passkeyDeviceKeys に現在のブラウザ (request UA) を追加 (best effort)
   * サーバ側で UA を normalize してから配列に push する
   */
  private async requestAddPasskeyDevice(): Promise<void> {
    const tenantId =
      this.me?.currentTenantId ?? this.me?.memberships[0]?.tenantId;
    if (!tenantId) {
      console.warn("requestAddPasskeyDevice skipped: no tenantId");
      return;
    }
    try {
      await apiPatch(AUTH_ME, { tenantId, addPasskeyDevice: true });
    } catch (err) {
      console.warn("requestAddPasskeyDevice failed:", err);
    }
  }

  /**
   * DDB Users.passkeyDeviceKeys を全クリア (best effort)
   */
  private async requestClearPasskeyDevices(): Promise<void> {
    const tenantId =
      this.me?.currentTenantId ?? this.me?.memberships[0]?.tenantId;
    if (!tenantId) {
      console.warn("requestClearPasskeyDevices skipped: no tenantId");
      return;
    }
    try {
      await apiPatch(AUTH_ME, { tenantId, clearPasskeyDevices: true });
    } catch (err) {
      console.warn("requestClearPasskeyDevices failed:", err);
    }
  }

  /**
   * 8. トークンリフレッシュチェック（初期遷移時に呼び出す）
   */
  async checkAndRefreshToken(): Promise<boolean> {
    try {
      // localStorageからトークンを取得
      const accessToken = localStorage.getItem("portal-accessToken");
      const refreshToken = localStorage.getItem("portal-refreshToken");

      if (!accessToken || !refreshToken) {
        return false; // トークンがない場合は再ログイン必要
      }

      // トークンの有効期限をチェック
      const decoded = decodeJWT(accessToken);
      const now = Math.floor(Date.now() / 1000);
      const expiresAt = decoded.payload.exp as number;
      const shouldRefresh = expiresAt - now < 300; // 5分前

      if (shouldRefresh) {
        console.log("Access token expiring soon, refreshing...");
        const newTokens = await callAuthRefresh(refreshToken, AUTH_REFRESH);

        // 新しいトークンを保存
        localStorage.setItem("portal-accessToken", newTokens.access);
        localStorage.setItem("portal-idToken", newTokens.id);
        if (newTokens.refresh) {
          localStorage.setItem("portal-refreshToken", newTokens.refresh);
        }

        // 内部の状態も更新
        this.accessToken = newTokens.access;
        this.idToken = newTokens.id;

        console.log("Token refreshed successfully");
      }

      return true; // リフレッシュ成功または不要
    } catch (error) {
      console.error("Token refresh failed:", error);
      return false; // リフレッシュ失敗
    }
  }

  /**
   * 9. サインアウトする
   */
  async signOut(opts: { reason?: string; global?: boolean } = {}) {
    // PRODUCT_DEV-549 計測: global:true は GlobalSignOut（全デバイス・全トークンを
    // 一括 revoke）。発火源(reason)とコールスタックを dev のみ永続記録する。
    // ※ 既定 global=true のまま（挙動は不変・計測専用）。
    const { reason = "unknown", global = true } = opts;
    trace("auth-signout", reason, { global }, { stack: true });
    // 全てのプロダクトからサインアウト
    await signOut({ global });
    // Amplify の signOut は portal-* キーをクリアしないため明示的に削除する
    localStorage.removeItem("portal-accessToken");
    localStorage.removeItem("portal-idToken");
    localStorage.removeItem("portal-refreshToken");
  }

  /**
   * 10. パスワードリセットを開始し、メールにコードを送信
   */
  async resetPassword(username: string): Promise<void> {
    await resetPassword({ username });
  }

  /**
   * 11. リセットコードと新しいパスワードでパスワードをリセット
   */
  async confirmResetPassword(
    username: string,
    code: string,
    newPassword: string,
  ): Promise<void> {
    await confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword,
    });
  }

  /**
   * 12. 認証済みユーザーのパスワード変更（PRODUCT_DEV-293）
   * 現在のセッションのアクセストークンを使ってCognitoのChangePasswordを呼ぶ
   */
  async changePassword(
    oldPassword: string,
    newPassword: string,
  ): Promise<void> {
    await updatePassword({ oldPassword, newPassword });
  }
}
